/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */

import React, { useState } from "react";
import ICONS from "../../../assets/Icons";
import { standardAccountNumberFormat } from "../../../utils/standardAccountNumberFormat";
import CommonBankLogo from "../CommonBankLogo";
import PositiveNegativeButton from "../CommonButtons/PositiveNegativeButton";
import PrimaryButton from "../CommonButtons/PrimaryButton";
import SecondaryRightIconSmallButton from "../CommonButtons/SecondaryRightIconSmallButton";
import FormatAmount from "../FormatAmount/FormatAmount";
import SVGComponent from "../SVGComponent";
import TitleSubtitlePair from "../TitleSubtitlePair";
import Loader from "../../Loader/Index";
import CommonAlertsStrip from "../CommonAlertsStrip";
import formatDateViewAll from "../../../utils/formatDateViewAll";
import CardActionableButton from "../CommonViewAllCard/CardSubComponent/CardActionableButton";
import useServiceChecker from "../../ServiceChecker";
import { GlobalConfiguration } from "../../../utils/configurationProvider";

const shouldBeRedacted = (price) => {
  return (
    price === "xxxx.xx" ||
    isNaN(+price) ||
    price === null ||
    price === undefined
  );
};

/**
 * @typedef {("textSubtext" | "textSubtextX2"  | "cta" | "ctaX2")} EntryType
 * @typedef {("button" | "submit")} BtnType
 * @typedef {Object} Entry
 * @property {String} text Text to display
 * @property {String} subText Subtext to display
 * @property {String} [textColor] Color of text (with the var())
 * @property {String} [subText1] Second subtext to display
 * @property {String} [text1] Second text to display
 * @property {String} [text1Color] Color of second text (with the var())
 * @property {String} [icon] Icon to display
 * @property {String} [iconColor] Colour of icon
 * @property {OnClickEventHandler} [onClick] What to do on click
 * @property {OnClickEventHandler} [onClick1] What to do on click (right side)
 * @property {String} [ifsc] Ifsc number to generate bank logo
 * @property {boolean} [isBold] Is text bold
 * @property {boolean} [isBoldR] Is right text bold
 * @property {EntryType} type Type of this entry, one of ("textSubtext" | "textSubtextX2" | "accountNumber" | "amount" | "cta")
 * @property {boolean} [isDisabled] is this disabled (useful for type = cta)
 *
 * @typedef {Object} Alert1
 * @property {String} text Text to show
 * @property {String} [icon] Icon to show, defaults to alert icon
 * @property {String} iconColor Icon colour
 * @property {boolean} isLoading Is this alert loading
 * @property {OnClickEventHandler} onClick What to do on click
 *
 *
 * @param {Object} props
 * @param {Entry[][]} props.entries List of list of `Entry`, 3 items in the outer list corresponds to the 3 columns in this card.
 * @param {OnClickEventHandler} props.approveClick What to do on approve button click
 * @param {OnClickEventHandler} props.rejectClick What to do on reject button click
 * @param {String} props.ctaBtn1Text  Top right cta button text
 * @param {OnClickEventHandler} props.ctaBtn1OnClick Top right cta button on click
 * @param {String} props.ctaBtn2Text top right cta button 2  text
 * @param {OnClickEventHandler} props.ctaBtn2OnClick top right cta button 2 on click
 * @param {("default" | "single" | "double")} props.cardState if this is "default" then approve reject button is shown, if this is "single" then account approval pending screen is shown, if this is "double" reject reason screen is shown
 * @param {String} props.overlay1Text Text on overlay 1 (account approval pending screen)
 * @param {String} [props.overlay1TextColor] Text color of overlay 1
 * @param {String} props.overlay1Subtext subtext on overlay 1 (account approval pending screen)
 * @param {String} [props.overlay1SubtextColor] sub text color of overlay 1
 * @param {String} [props.overlay1Icon] Icon on overlay 1 (account approval pending screen)
 * @param {String} [props.overlay1IconColor] Icon color on overlay 1 (account approval pending screen)

 * @param {String} props.overlay1Cta1Text text on overlay 1 cta button 1 (approval not possible screen)
 * @param {Boolean} props.overlay1Cta1Inactive is overlay 1 cta button 1 inactive (approval not possible screen)
 * @param {Boolean} props.overlay1Cta1Disabled is overlay 1 cta button 1 disabled (approval not possible screen)
 * @param {OnClickEventHandler} props.overlay1Cta1OnClick what to do on overlay 1 cta button 1 click (approval not possible screen)
 * @param {boolean} props.overlay1Cta1Loading is overlay 1 cta button 1 loading (approval not possible screen)
 * @param {string} props.overlay1Cta1Color overlay 1 cta button 1 bg color
 * 
 * @param {String} props.overlay1CtaText text on overlay 1 cta button (account approval pending screen)
 * @param {Boolean} props.overlay1CtaInactive is overlay 1 cta button inactive (account approval pending screen)
 * @param {Boolean} props.overlay1CtaDisabled is overlay 1 cta button disabled (account approval pending screen)
 * @param {OnClickEventHandler} props.overlay1CtaOnClick what to do on overlay 1 cta button click (account approval pending screen)
 * @param {boolean} props.overlay1CtaLoading is overlay 1 cta button loading (account approval pending screen)
 * @param {string} props.overlay1CtaColor overlay 1 cta button  bg color
 * 
 * @param {String} props.overlay2Text overlay 2 text (reject reason screen)
 * @param {String} props.overlay2Subtext overlay 2 subtext (reject reason screen)
 * @param {import("../CommonAlertsStrip").Alert[]} props.alerts list of Alerts.
 * @param {String} props.ownerAccountNumber,
 * @param {String} props.ownerAccountIfsc,
 * @param {String} props.ownerAccountNumberSubtext,
 * @param {String} props.ownerAccountBalance,
 * @param {String} props.ownerAccountBalanceSubtext,
 * @param {OnClickEventHandler} props.ownerAccountBalanceOnClick - what to do on owner account balance refresh
 * @param {String} props.destinationAccountNumber,
 * @param {String} props.destinationAccountIfsc,
 * @param {String} props.destinationAccountNumberSubtext,
 * @param {String} props.destinationAccountText,
 * @param {String} props.destinationAccountSubtext,
 * @param {boolean} props.thisIsARecreateCard - Is this used for recreate txn
 * @param {OnClickEventHandler} props.recreateOnClick What to do on recreate btn click
 * @param {String} props.recreateBtnText
 * @param {boolean} props.recreateBtnIsDisabled 
 * @param {String} props.rejectBtnText
 * @param {String} props.approveBtnText
 * @param {boolean} props.accountApprovalPending - Is account approval pending
 * 
  * @param {string} props.createdOnText
  * @param {string} props.createdOnSubtext,
  * 
  * @param {string} props.createdByText,
  * @param {string} props.createdBySubtext,
  * 
  * 
  * @param {boolean} props.overrideAutoLoader Take manual control of approve / reject loading animation
  * @param {("none" | "approve" | "reject")} props.whichBtnClicked - Whether approve or reject or none button is clicked
  * influcences what color loader is shown, only useful is overrideAutoLoader is true
  * 
  * @param {"none" | "loading" | "up" | "down"} [props.ownerAccountRefreshState] - Controls the animation next to the owner account balance. (up arrow, down arrow, loader)
 * @returns {JSX.Element}
 */
export default function CommonTransactionApprovalCard({
  entries,
  rejectClick,
  approveClick,
  ctaBtn1OnClick,
  ctaBtn1Text,
  ctaBtn2OnClick,
  ctaBtn2Text,
  alerts,
  cardState,
  ownerAccountNumber,
  ownerAccountIfsc,
  ownerAccountNumberSubtext,
  ownerAccountBalance,
  ownerAccountBalanceSubtext,
  ownerAccountBalanceOnClick,
  destinationAccountNumber,
  destinationAccountIfsc,
  destinationAccountNumberSubtext,
  destinationAccountText,
  destinationAccountSubtext,

  overlay1CtaDisabled,
  overlay1CtaInactive,
  overlay1CtaLoading,
  overlay1CtaOnClick,
  overlay1CtaText,
  overlay1CtaColor,

  overlay1Cta1Disabled,
  overlay1Cta1Inactive,
  overlay1Cta1Loading,
  overlay1Cta1OnClick,
  overlay1Cta1Text,
  overlay1Cta1Color,

  overlay1Text,
  overlay1TextColor,
  overlay1Subtext,
  overlay1Icon,
  overlay1IconColor,
  overlay1SubtextColor,

  overlay2Subtext,
  overlay2Text,

  thisIsARecreateCard,
  recreateOnClick,
  recreateBtnText,
  recreateBtnIsDisabled,

  rejectBtnText,
  approveBtnText,

  accountApprovalPending,

  createdOnText,
  createdOnSubtext,
  createdBySubtext,
  createdByText,
  overrideAutoLoader,
  whichBtnClicked,
  ownerAccountRefreshState,
}) {
  const createdByRequired = createdByText !== undefined;
  const createdOnRequired = createdOnText !== undefined;

  const intercalate = (arr, sep) =>
    arr.reduce((a, v) => [...a, v, sep], []).slice(0, -1);
  /**
   *
   * @param {Entry} entry
   * @param {boolean} halfWidth
   * @param {boolean} giveExcessPadding
   */
  function processEntry(entry, halfWidth, giveExcessPadding = false) {
    if (entry.textColor?.startsWith("var") ?? false) {
      entry.textColor = entry.textColor?.slice(6, -1)?.trim();
    }
    if (entry.text1Color?.startsWith("var") ?? false) {
      entry.text1Color = entry.text1Color?.slice(6, -1)?.trim();
    }
    const ctaDisabled = clickedBtn !== "none";
    switch (entry.type) {
      case "textSubtext":
        return (
          <div
            className={`flex flex-row justify-between items-start gap-gap-sm ${giveExcessPadding && "pt-padding-sm"} ${halfWidth ? "flex-1 min-w-0" : "w-full"}`}
          >
            <TitleSubtitlePair
              enableTicker
              title={entry.text}
              titleColor={entry.textColor ?? "text-primary"}
              subtitle={entry.subText}
              variant="sm"
              isBold={entry.isBold ?? false}
            />
            {entry.icon && (
              <SVGComponent
                color={entry.iconColor ?? ""}
                url={entry.icon ?? ""}
                width="var(--icon-md)"
                height={"var(--icon-md)"}
              />
            )}
          </div>
        );
      case "textSubtextX2":
        return (
          <div
            className={`flex flex-row  items-center gap-gap-md justify-start ${halfWidth ? "flex-1 min-w-0" : "w-full"} Qlg:pr-padding-sm pr-padding-sm`}
          >
            <div className="w-width-md flex-shrink-0 flex-grow-0">
              <TitleSubtitlePair
                enableTicker
                title={entry.text}
                subtitle={entry.subText}
                titleColor={entry.textColor ?? "text-primary"}
                variant="sm"
                isBold={entry.isBold ?? false}
              />
            </div>
            <div className="w-width-md flex-shrink-0 flex-grow-0">
              <TitleSubtitlePair
                alignRight
                textAlign="right"
                enableTicker
                titleColor={entry.text1Color ?? "text-primary"}
                title={entry.text1 ?? "text 1"}
                subtitle={entry.subText1 ?? "subtext 1"}
                variant="sm"
                isBold={entry.isBoldR ?? false}
              />
            </div>
          </div>
        );
      case "ctaX2":
        return (
          <div
            className={`flex flex-row  items-center gap-gap-sm justify-start ${halfWidth ? "flex-1 min-w-0" : "w-full"} ${entry.onClick ? "" : "pl-padding-sm"} ${entry.onClick1 ? "" : "pr-padding-sm"}`}
          >
            <div className="w-width-md flex-shrink-0 flex-grow-0">
              <TitleSubtitlePair
                enableTicker
                isDisabled={ctaDisabled}
                title={entry.text}
                subtitle={entry.subText}
                titleColor={entry.textColor ?? "text-primary"}
                variant="sm"
                isBold={entry.isBold ?? false}
                onClick={ctaDisabled ? undefined : entry.onClick}
                hoverOption="white"
                showArrow={entry.onClick}
              />
            </div>
            <div className="w-width-md flex-shrink-0 flex-grow-0">
              <TitleSubtitlePair
                alignRight
                isDisabled={ctaDisabled}
                textAlign="right"
                enableTicker
                titleColor={entry.text1Color ?? "text-primary"}
                title={entry.text1 ?? "text 1"}
                subtitle={entry.subText1 ?? "subtext 1"}
                variant="sm"
                isBold={entry.isBoldR ?? false}
                onClick={ctaDisabled ? undefined : entry.onClick1}
                hoverOption="white"
                showArrow={entry.onClick1}
              />
            </div>
          </div>
        );
      case "accountNumber":
      case "amount":
      case "cta":
      default:
        return (
          <div
            className={`z-z-index-content relative  ${halfWidth ? "flex-1 min-w-0" : "w-full"} `}
          >
            <TitleSubtitlePair
              enableTicker={true}
              hoverOption="white"
              showArrow={true}
              isDisabled={ctaDisabled || (entry.isDisabled ?? false)}
              onClick={ctaDisabled ? undefined : (entry?.onClick ?? undefined)}
              subtitle={entry.subText}
              variant="sm"
              title={entry.text}
              titleColor={entry.textColor ?? "text-primary"}
              isBold={entry.isBold ?? false}
            />
          </div>
        );
    }
  }
  /**
   *
   * @param {"single" | "double" | "default"} cardState
   */
  function getWidthBasedOnCardState(cardState) {
    switch (cardState) {
      case "default":
        return "scale-0";
      case "single":
        return "flex w-[calc(1*(var(--std-width)/3))] scale-full-scale";
      case "double":
        return "flex w-[calc(2*(var(--std-width)/3))] scale-full-scale";
      default:
        break;
    }
  }

  function getSourceBalanceRefreshGif(state) {
    switch (state) {
      case "up":
        return ICONS?.ARROW_UP_GREEN_GIF;
      case "down":
        return ICONS?.ARROW_DOWN_RED_GIF;
      case "loading":
        return ICONS?.LOADER_GIF;
      case "none":
      default:
        return undefined;
    }
  }

  const [btnClicked, setbtnClicked] = useState("none");

  let clickedBtn;
  if (overrideAutoLoader) {
    clickedBtn = whichBtnClicked;
  } else {
    clickedBtn = btnClicked;
  }

  const [ensureAll] = useServiceChecker();

  const service =
    ensureAll(["VAccountBalance"]).status || ensureAll(["Admin"]).status;

  function getOwnerAccountBalanceColor() {
    if (shouldBeRedacted(ownerAccountBalance)) {
      return "text-settled";
    }

    // get entry with amount in it.
    const amountEntries = entries
      .flat()
      .filter(
        (x) =>
          x.subText?.toLowerCase()?.includes("amount") ||
          x.subText1?.toLowerCase()?.includes("amount"),
      );
    const inrRegex = /INR (\d+\.(?=\d+)\d{1,2}$)/;
    if (amountEntries.length === 1) {
      const amount =
        inrRegex.exec(amountEntries[0]?.text?.replaceAll(",", ""))?.[1] ??
        "00.00";
      return +amount <= +ownerAccountBalance ? "text-settled" : "text-failed";
    }
    return "text-primary";
  }

  return (
    <div className="Qlg:w-std-width relative z-z-index-base w-auto bg-primary-nav h-full flex flex-col rounded-border-radius-md text-text-primary box-border">
      <div className="w-full bg-primary-card h-full hidden Qlg:flex Qlg:flex-col flex-col justify-between rounded-border-radius-md  relative">
        <div
          className={`h-full bg-primary-black opacity-dark-opacity z-z-index-overlay absolute bottom-bottom-base left-left-base rounded-l-border-radius-md ${cardState === "default" ? "w-width-base hidden" : accountApprovalPending && "w-[calc(1*(var(--std-width)/3))] block"}`}
        ></div>
        <div
          className={`z-z-index-overlay right-right-base bottom-bottom-base h-full  ${accountApprovalPending ? "bg-primary-card " : "bg-primary-nav-act"} absolute ${getWidthBasedOnCardState(cardState)} rounded-r-border-radius-md transition-all duration-animation-medium origin-bottom-right`}
        >
          {cardState === "double" && (
            <div className="w-width-md border-r border-primary-bg my-margin-md">
              <div className="flex flex-col justify-start items-start gap-gap-sm flex-grow-0 w-full max-w-full bg-primary-nav-act px-padding-md">
                <div className="text-font-sm text-text-primary font-semibold  break-words whitespace-normal w-full">
                  {overlay2Subtext ?? "Overlay 2 subtext"}
                </div>
                <div className="text-font-xs text-text-primary break-words">
                  {overlay2Text ?? "Overlay 2 text"}
                </div>
              </div>
            </div>
          )}
          <div
            className={`flex flex-row justify-start items-start gap-gap-sm flex-grow-0 ${accountApprovalPending ? "border-l border-primary-bg" : ""} ${cardState === "double" ? "w-width-md" : "w-full"} max-w-full  px-padding-md my-margin-md rounded-r-border-radius-md`}
          >
            {overlay1Icon && (
              <div className="flex-shrink-0">
                <SVGComponent
                  url={overlay1Icon}
                  width="var(--icon-lg)"
                  height="var(--icon-lg)"
                  color={overlay1IconColor}
                />
              </div>
            )}
            <div className="flex flex-col gap-gap-sm justify-between h-full w-full">
              <div
                style={{
                  color: overlay1TextColor || "var(--text-primary)",
                }}
                className={`${overlay1TextColor ? "text-[" + overlay1TextColor + "]" : "text-text-primary"} font-semibold text-font-md`}
              >
                {overlay1Text ?? "Overlay 1 heading"}
              </div>
              <div
                style={{
                  // hyphens: "auto",
                  color: overlay1SubtextColor || "var(--text-primary)",
                }}
                className={`text-font-sm ${overlay1SubtextColor ? "text-[" + overlay1SubtextColor + "]" : "text-text-primary"}`}
              >
                {overlay1Subtext ?? "Overlay 1 subtext"}
              </div>
              <div className="flex-grow" />
              {overlay1Cta1Text && (
                <PrimaryButton
                  hoverOption="white"
                  btnText={overlay1Cta1Text ?? "Do action 1"}
                  btnType="button"
                  enabledBgColor={overlay1Cta1Color || "var(--primary-bg)"}
                  enabledTextColor="var(--text-primary)"
                  fullWidthRequired
                  handleFunction={overlay1Cta1OnClick ?? undefined}
                  isLoading={overlay1Cta1Loading}
                  inActiveVariantNeeded={overlay1Cta1Inactive}
                  isDisabled={overlay1Cta1Disabled}
                />
              )}
              <PrimaryButton
                hoverOption="white"
                btnText={overlay1CtaText ?? "Do action"}
                btnType="button"
                enabledBgColor={overlay1CtaColor || "var(--primary-bg)"}
                enabledTextColor="var(--text-primary)"
                fullWidthRequired
                handleFunction={overlay1CtaOnClick ?? undefined}
                isLoading={overlay1CtaLoading}
                inActiveVariantNeeded={overlay1CtaInactive}
                isDisabled={overlay1CtaDisabled}
              />
            </div>
          </div>
        </div>
        {intercalate(
          [
            ...entries.map((entryList, i) => {
              return (
                <div className="flex flex-row w-full flex-1">
                  <div
                    className={` my-margin-md  w-modules-popup-width ${entryList[0].type.includes("cta") ? "pl-padding-sm pr-padding-md" : "px-padding-md"}`}
                  >
                    {processEntry(entryList[0], false)}
                  </div>
                  <div
                    className={`w-modules-popup-width my-margin-md ${entryList[1].type.includes("cta") ? "pl-padding-sm pr-padding-md" : "px-padding-md"}  ${cardState === "default" ? "border-l border-r" : accountApprovalPending ? "" : "border-l"} border-primary-bg `}
                  >
                    {processEntry(entryList[1], false)}
                  </div>
                  <div
                    className={`w-modules-popup-width my-margin-sm ${entryList[2].type.includes("cta") ? "pl-padding-sm pr-padding-md" : "px-padding-md  "} flex flex-row justify-between gap-gap-sm`}
                  >
                    {processEntry(entryList[2], i === 0, true)}
                    {i === 0 && cardState === "default" && (
                      <div
                        className={`flex flex-col  ${ctaBtn2Text ? "gap-gap-xs" : "gap-gap-sm"} flex-shrink-0`}
                      >
                        {!ctaBtn2Text && <div />}
                        <SecondaryRightIconSmallButton
                          isDisabled={clickedBtn !== "none"}
                          btnText={ctaBtn1Text ?? "Cta button 1"}
                          btnType="button"
                          enabledBgColor="var(--primary-nav-act)"
                          icon={ICONS.ARROW_RIGHT_WHITE}
                          imgColor="var(--text-primary)"
                          hoverOption="white"
                          enabledTextColor="var(--text-primary)"
                          handleFunction={
                            clickedBtn !== "none"
                              ? undefined
                              : (ctaBtn1OnClick ?? undefined)
                          }
                        />
                        {ctaBtn2Text && (
                          <SecondaryRightIconSmallButton
                            isDisabled={clickedBtn !== "none"}
                            btnText={ctaBtn2Text ?? "Cta button 2"}
                            btnType="button"
                            enabledBgColor="var(--primary-nav-act)"
                            icon={ICONS.ARROW_RIGHT_WHITE}
                            imgColor="var(--text-primary)"
                            hoverOption="white"
                            enabledTextColor="var(--text-primary)"
                            handleFunction={
                              clickedBtn !== "none"
                                ? undefined
                                : (ctaBtn2OnClick ?? undefined)
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            }),
            <>
              <div className="flex flex-row w-full">
                <div
                  className={`flex flex-row ${thisIsARecreateCard ? "w-full " : "w-full"}`}
                >
                  <div
                    className={`flex flex-col items-center w-[calc(2*(var(--std-width)/3))] flex-shrink-0 ${cardState === "default" ? "border-r" : ""} border-primary-bg my-margin-md`}
                  >
                    <div className="flex flex-row w-full">
                      <div
                        className={`px-padding-md ${createdByRequired && "pb-padding-md"}   ${cardState !== "double" ? "border-r" : ""} border-primary-bg w-full`}
                      >
                        {!createdByRequired && (
                          <>
                            <div className="flex flex-row justify-between gap-gap-md items-center self-center">
                              <div className="flex flex-col gap-gap-xs">
                                <TitleSubtitlePair
                                  variant="sm"
                                  title={standardAccountNumberFormat(
                                    ownerAccountNumber,
                                    true,
                                    true,
                                  )}
                                  subtitle={ownerAccountNumberSubtext}
                                />
                              </div>

                              <CommonBankLogo
                                ifsc={ownerAccountIfsc || ""}
                                variant="big"
                                type="pill"
                              />
                            </div>
                          </>
                        )}

                        {createdByRequired && (
                          <>
                            <div className="flex flex-col gap-gap-xs">
                              <TitleSubtitlePair
                                variant="sm"
                                title={createdByText || "NA"}
                                subtitle={createdBySubtext || "Created by"}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      <div className="px-padding-md  w-full">
                        <div className="flex flex-row justify-between gap-gap-md items-center self-center">
                          <div className="flex flex-col gap-gap-xs">
                            <TitleSubtitlePair
                              variant="sm"
                              title={standardAccountNumberFormat(
                                destinationAccountNumber || "00000000000",
                                true,
                                true,
                              )}
                              subtitle={
                                destinationAccountNumberSubtext ||
                                "Destination account"
                              }
                            />
                          </div>

                          <CommonBankLogo
                            ifsc={destinationAccountIfsc || ""}
                            variant="big"
                            type="pill"
                          />
                        </div>
                      </div>
                    </div>

                    {!createdByRequired && (
                      <>
                        <div
                          className={`${cardState === "double" ? "invisible" : accountApprovalPending ? "invisible" : "visible"} my-margin-sm`}
                        >
                          <SVGComponent
                            url={ICONS?.ARROW_RIGHT_BLUE_WHITE}
                            color=""
                            height={"var(--icon-md)"}
                            width="var(--icon-md)"
                          />
                        </div>
                      </>
                    )}

                    <div className="flex flex-row w-full">
                      <div
                        className={`px-padding-md  ${cardState !== "double" ? "border-r" : ""} border-primary-bg w-full`}
                      >
                        {!createdByRequired && (
                          <>
                            <div className="flex flex-row justify-between">
                              <div className="flex flex-col gap-gap-xs">
                                <div className="flex flex-row gap-gap-sm items-center">
                                  <div
                                    className={` ${getOwnerAccountBalanceColor()}  ${
                                      ownerAccountBalance
                                        ? ownerAccountBalance <=
                                            GlobalConfiguration?.lengthCheckZero &&
                                          !shouldBeRedacted(ownerAccountBalance)
                                          ? "font-semibold"
                                          : "font-semibold"
                                        : "text-text-primary font-normal"
                                    }
                                  
                                   ${!shouldBeRedacted(ownerAccountBalance) ? "text-font-sm  border-primary-bg" : ""}`}
                                  >
                                    {shouldBeRedacted(ownerAccountBalance)
                                      ? ""
                                      : "INR"}{" "}
                                    {FormatAmount({
                                      price: ownerAccountBalance,
                                    })}
                                  </div>
                                  {!shouldBeRedacted(ownerAccountBalance) &&
                                    getSourceBalanceRefreshGif(
                                      ownerAccountRefreshState,
                                    ) && (
                                      <>
                                        <span>
                                          <img
                                            src={getSourceBalanceRefreshGif(
                                              ownerAccountRefreshState,
                                            )}
                                            className={
                                              ownerAccountRefreshState ===
                                              "loading"
                                                ? "w-icon-sm h-icon-sm"
                                                : `w-icon-md h-icon-md`
                                            }
                                            alt=""
                                          />
                                        </span>
                                      </>
                                    )}
                                </div>
                                <div className="text-text-secondary text-font-xs">
                                  {ownerAccountBalanceSubtext ??
                                    "Balance - Some time ago"}
                                </div>
                              </div>
                              <div>
                                <CardActionableButton
                                  btnType="button"
                                  handleFunction={
                                    ownerAccountBalanceOnClick ?? undefined
                                  }
                                  isDisabled={!service}
                                  disabledBgColor="var(--primary-nav-act)"
                                  icon={ICONS?.RELOAD_REFRESH_WHITE}
                                  hoverOption="white"
                                  title="Refresh"
                                  bgColor="var(--primary-nav)"
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {createdOnRequired && (
                          <>
                            <div className="flex flex-col gap-gap-xs">
                              <div className="flex flex-row gap-gap-sm items-center">
                                <TitleSubtitlePair
                                  variant="sm"
                                  title={formatDateViewAll(
                                    createdOnText,
                                    true,
                                    true,
                                  )}
                                  subtitle={createdOnSubtext}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="px-padding-md   w-full">
                        <div className="flex flex-row justify-start gap-gap-md items-center self-center">
                          <div className="flex flex-col gap-gap-xs">
                            <TitleSubtitlePair
                              variant="sm"
                              title={destinationAccountText}
                              subtitle={destinationAccountSubtext}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {thisIsARecreateCard && (
                    <div className="w-full my-margin-md px-padding-md flex items-end ">
                      <PrimaryButton
                        isDisabled={recreateBtnIsDisabled}
                        enabledBgColor="var(--primary-bg)"
                        btnText={recreateBtnText || "Recreate this transaction"}
                        btnType="button"
                        handleFunction={recreateOnClick ?? undefined}
                        fullWidthRequired
                        hoverOption="white"
                      />
                    </div>
                  )}
                </div>
                <div className="absolute bottom-0 right-0 flex w-approval-card-slot-width justify-end">
                  {thisIsARecreateCard || cardState !== "default" ? (
                    <>
                      {/* <div className="p-padding-md w-full">
                        <PrimaryButton
                          enabledBgColor="var(--primary-nav)"
                          btnText="Recreate this transaction"
                          btnType="button"
                          handleFunction={(e) => {}}
                          fullWidthRequired
                        />
                      </div> */}
                    </>
                  ) : clickedBtn !== "none" ? (
                    <>
                      <div className="w-max-button-width-for-approval-animation flex">
                        <div
                          className={`w-width-full rounded-br-border-radius-md rounded-tl-border-radius-md flex justify-center items-center ${
                            clickedBtn === "reject"
                              ? "bg-negative-act"
                              : "bg-positive-act"
                          } py-padding-lg`}
                        >
                          <Loader
                            height="var(--icon-lg)"
                            width="var(--icon-lg)"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <PositiveNegativeButton
                        borderRadiusTopLeft
                        btnText={rejectBtnText || "Reject transaction"}
                        icon={ICONS.REJECT_ICON_RED_WITH_WHITE_CIRCLE}
                        handleFunction={() => {
                          setbtnClicked("reject");
                          if (typeof rejectClick === "function") {
                            rejectClick();
                          }
                        }}
                        enabledBgColor="var(--negative-act)"
                      />
                      <PositiveNegativeButton
                        borderRadiusBottomRight
                        btnText={approveBtnText || "Approve transaction"}
                        icon={ICONS.APPROVE_CIRCLE_GREEN_CHECKMARK}
                        handleFunction={() => {
                          setbtnClicked("approve");
                          if (typeof approveClick === "function") {
                            approveClick();
                          }
                        }}
                        enabledBgColor="var(--positive-act)"
                      />
                    </>
                  )}
                </div>
              </div>
            </>,
          ],
          <div className="mx-margin-md">
            <hr className="border-primary-bg" />
          </div>,
        )}
      </div>
      <div className="Qlg:hidden flex flex-col gap-gap-sm bg-primary-card h-full  rounded-border-radius-md">
        <div className=""></div>
        {intercalate(
          entries.flat().map((entry, i) => {
            if (i === 2 && cardState === "default") {
              return (
                <div
                  className={`flex flex-row gap-gap-sm w-full justify-between ${entry.type === "cta" ? "px-padding-sm" : "px-padding-md"}`}
                >
                  {processEntry(entry, true)}
                  <div className="flex flex-col gap-gap-xs flex-shrink-0">
                    <SecondaryRightIconSmallButton
                      isDisabled={clickedBtn !== "none"}
                      btnText={ctaBtn1Text ?? "Cta button 1"}
                      btnType="button"
                      enabledBgColor="var(--primary-nav-act)"
                      icon={ICONS.ARROW_RIGHT_WHITE}
                      imgColor="var(--text-primary)"
                      hoverOption="white"
                      enabledTextColor="var(--text-primary)"
                      handleFunction={
                        clickedBtn !== "none"
                          ? undefined
                          : (ctaBtn1OnClick ?? undefined)
                      }
                    />
                    {ctaBtn2Text && (
                      <SecondaryRightIconSmallButton
                        isDisabled={clickedBtn !== "none"}
                        btnText={ctaBtn2Text ?? "Cta button 2"}
                        btnType="button"
                        enabledBgColor="var(--primary-nav-act)"
                        icon={ICONS.ARROW_RIGHT_WHITE}
                        imgColor="var(--text-primary)"
                        hoverOption="white"
                        enabledTextColor="var(--text-primary)"
                        handleFunction={
                          clickedBtn !== "none"
                            ? undefined
                            : (ctaBtn2OnClick ?? undefined)
                        }
                      />
                    )}
                  </div>
                </div>
              );
            } else
              return (
                <div
                  className={`${entry.type === "cta" ? "px-padding-sm" : "px-padding-md"}`}
                >
                  {processEntry(entry)}
                </div>
              );
          }),
          <div className="px-padding-md">
            <hr className="border-primary-bg" />
          </div>,
        )}
        <div className="px-padding-md">
          <hr className="border-primary-bg" />
        </div>
        <div className="flex flex-col gap-gap-sm px-padding-md">
          {!createdByRequired && (
            <>
              <div className="flex flex-row justify-between items-center">
                <TitleSubtitlePair
                  title={standardAccountNumberFormat(
                    ownerAccountNumber ?? "000000000",
                    true,
                    true,
                  )}
                  subtitle={ownerAccountNumberSubtext ?? "Source account"}
                />
                <CommonBankLogo
                  ifsc={ownerAccountIfsc || ""}
                  variant="big"
                  type="pill"
                />
              </div>
            </>
          )}

          {createdByRequired && (
            <>
              <div className="flex flex-col gap-gap-xs">
                <TitleSubtitlePair
                  variant="sm"
                  title={createdByText || "NA"}
                  subtitle={createdBySubtext || "Created by"}
                />
              </div>
            </>
          )}

          {!createdByRequired && (
            <>
              <div className="flex flex-row justify-between">
                <div className="flex flex-col gap-gap-xs">
                  <div className="flex flex-row gap-gap-sm items-center">
                    <div
                      className={` ${
                        ownerAccountBalance
                          ? ownerAccountBalance <
                              GlobalConfiguration?.lengthCheckZero &&
                            !shouldBeRedacted(ownerAccountBalance)
                            ? "text-failed font-semibold"
                            : "text-settled font-semibold"
                          : "text-text-primary font-normal"
                      }, ${!shouldBeRedacted(ownerAccountBalance) ? "text-font-sm border-primary-bg" : ""}`}
                    >
                      {shouldBeRedacted(ownerAccountBalance) ? "" : "INR"}{" "}
                      {FormatAmount({ price: ownerAccountBalance })}
                    </div>
                    {!shouldBeRedacted(ownerAccountBalance) &&
                      getSourceBalanceRefreshGif(ownerAccountRefreshState) && (
                        <>
                          <span>
                            <img
                              src={getSourceBalanceRefreshGif(
                                ownerAccountRefreshState,
                              )}
                              className={
                                ownerAccountRefreshState === "loading"
                                  ? "w-icon-sm h-icon-sm"
                                  : `w-icon-md h-icon-md`
                              }
                              alt=""
                            />
                          </span>
                        </>
                      )}
                  </div>
                  <div className="text-text-secondary text-font-xs">
                    {ownerAccountBalanceSubtext ?? "Balance - Some time ago"}
                  </div>
                </div>
                <div>
                  <CardActionableButton
                    btnType="button"
                    bgColor="var(--primary-nav)"
                    handleFunction={ownerAccountBalanceOnClick ?? undefined}
                    isDisabled={!service}
                    disabledBgColor="var(--primary-nav-act)"
                    icon={ICONS?.RELOAD_REFRESH_WHITE}
                    hoverOption="white"
                    title="Refresh"
                  />
                </div>
              </div>
            </>
          )}
          {createdOnRequired && (
            <>
              <div className="flex flex-col gap-gap-xs">
                <div className="flex flex-row gap-gap-sm items-center">
                  <TitleSubtitlePair
                    variant="sm"
                    title={formatDateViewAll(createdOnText, true, true)}
                    subtitle={createdOnSubtext}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex flex-row gap-gap-sm w-full items-center px-padding-md">
          {createdOnRequired ? (
            <>
              <div className="w-full">
                <hr className="border-primary-bg" />
              </div>
            </>
          ) : (
            <>
              <div className="w-full">
                <hr className="border-primary-bg" />
              </div>
              <SVGComponent
                url={ICONS?.ARROW_DOWN_BLUE_WHITE}
                color=""
                height={"var(--icon-md)"}
                width="var(--icon-md)"
              />
              <div className="w-full">
                <hr className="border-primary-bg" />
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col gap-gap-sm px-padding-md">
          <div className="flex flex-row justify-between items-center">
            <TitleSubtitlePair
              variant="sm"
              title={standardAccountNumberFormat(
                destinationAccountNumber ?? "000000000",
                true,
                true,
              )}
              subtitle={
                destinationAccountNumberSubtext ?? "Destination account"
              }
            />
            <CommonBankLogo
              ifsc={destinationAccountIfsc || ""}
              variant="big"
              type="pill"
            />
          </div>
          <div className="flex flex-row items-center">
            <TitleSubtitlePair
              variant="sm"
              title={destinationAccountText ?? "destination account"}
              subtitle={destinationAccountSubtext ?? "sub text here"}
            />
          </div>
        </div>
        {cardState === "default" ? (
          thisIsARecreateCard ? (
            <>
              <div className="p-padding-md pt-padding-sm w-full">
                <PrimaryButton
                  isDisabled={recreateBtnIsDisabled}
                  enabledBgColor="var(--primary-bg)"
                  btnText={recreateBtnText || "Recreate this transaction"}
                  btnType="button"
                  handleFunction={recreateOnClick ?? undefined}
                  fullWidthRequired
                  hoverOption="white"
                />
              </div>
            </>
          ) : clickedBtn !== "none" ? (
            <>
              <div className="w-full flex justify-end">
                <div className="w-max-button-width-for-approval-animation flex">
                  <div
                    className={`w-width-full rounded-br-border-radius-md rounded-tl-border-radius-md flex justify-center items-center ${
                      clickedBtn === "reject"
                        ? "bg-negative-act"
                        : "bg-positive-act"
                    } py-padding-lg`}
                  >
                    <Loader
                      height="var(--icon-lg)"
                      width="var(--icon-lg)"
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-end w-full  p-padding-base">
                <PositiveNegativeButton
                  borderRadiusTopLeft
                  btnText={rejectBtnText || "Reject transaction"}
                  icon={ICONS.REJECT_ICON_RED_WITH_WHITE_CIRCLE}
                  handleFunction={() => {
                    setbtnClicked("Reject");
                    if (typeof rejectClick === "function") {
                      rejectClick();
                    }
                  }}
                  enabledBgColor="var(--negative-act)"
                />
                <PositiveNegativeButton
                  borderRadiusBottomRight
                  btnText={approveBtnText || "Approve transaction"}
                  icon={ICONS.APPROVE_CIRCLE_GREEN_CHECKMARK}
                  handleFunction={() => {
                    setbtnClicked("Approve");
                    if (typeof approveClick === "function") {
                      approveClick();
                    }
                  }}
                  enabledBgColor="var(--positive-act)"
                />
              </div>
            </>
          )
        ) : (
          <div
            className={
              "rounded-b-border-radius-md " +
              (accountApprovalPending
                ? "bg-primary-card"
                : `bg-primary-nav-act`)
            }
          >
            {cardState === "double" && (
              <>
                <div className="w-full py-padding-md pb-padding-sm">
                  <div className="flex flex-col justify-start items-start gap-gap-sm flex-grow-0 w-full max-w-full bg-primary-nav-act px-padding-md">
                    <div className="text-font-sm font-semibold text-text-primary">
                      {overlay2Subtext ?? "Overlay 2 subtext"}
                    </div>
                    <div className="text-font-xs text-text-primary break-words">
                      {overlay2Text ?? "Overlay 2 text"}
                    </div>
                  </div>
                </div>
                <div className="px-padding-md">
                  <hr className="border-primary-bg" />
                </div>
              </>
            )}
            <div
              className={`flex flex-row justify-start items-start gap-gap-sm flex-grow-0 w-full max-w-full  p-padding-md ${
                cardState === "double" ? "pt-padding-sm" : ""
              }`}
            >
              {overlay1Icon && (
                <div className="flex-shrink-0">
                  <SVGComponent
                    url={overlay1Icon}
                    width="var(--icon-lg)"
                    height="var(--icon-lg)"
                    color={overlay1IconColor}
                  />
                </div>
              )}
              <div className="flex flex-col gap-gap-sm justify-between h-full w-full">
                <div className="flex flex-col gap-gap-xs">
                  <div
                    style={{
                      color: overlay1TextColor || "var(--text-primary)",
                    }}
                    className={`${overlay1TextColor ? "text-[" + overlay1TextColor + "]" : "text-text-primary"} font-semibold text-font-sm`}
                  >
                    {overlay1Text ?? "Overlay 1 heading"}
                  </div>
                  <div
                    style={{
                      // hyphens: "auto",
                      color: overlay1SubtextColor || "var(--text-primary)",
                    }}
                    className={`text-font-sm  ${overlay1SubtextColor ? "text-[" + overlay1SubtextColor + "]" : "text-text-primary"}`}
                  >
                    {overlay1Subtext ?? "Overlay 1 subtext"}
                  </div>
                </div>

                {overlay1Cta1Text && (
                  <PrimaryButton
                    hoverOption="white"
                    btnText={overlay1Cta1Text ?? "Do action 1"}
                    btnType="button"
                    enabledBgColor={overlay1Cta1Color || "var(--primary-bg)"}
                    enabledTextColor="var(--text-primary)"
                    fullWidthRequired
                    handleFunction={overlay1Cta1OnClick ?? undefined}
                    isLoading={overlay1Cta1Loading}
                    inActiveVariantNeeded={overlay1Cta1Inactive}
                    isDisabled={overlay1Cta1Disabled}
                  />
                )}
                <PrimaryButton
                  hoverOption="white"
                  btnText={overlay1CtaText ?? "Do action"}
                  btnType="button"
                  enabledBgColor={overlay1CtaColor || "var(--primary-bg)"}
                  enabledTextColor="var(--text-primary)"
                  fullWidthRequired
                  handleFunction={overlay1CtaOnClick ?? undefined}
                  isLoading={overlay1CtaLoading}
                  inActiveVariantNeeded={overlay1CtaInactive}
                  isDisabled={overlay1CtaDisabled}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <CommonAlertsStrip alerts={alerts} />
    </div>
  );
}
